import { ClientEvents } from 'common/events/ClientEvents';
import { lte } from 'common/PatternMatchingUtilities';
import { assertDateString, currentUrlDate, formatDateTimeUrlDate } from 'common/UrlDate';
import { Slugs } from 'common/urls/Slugs';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect } from 'react';
import { RouteChildrenProps, useHistory } from 'react-router';
import { DishDescriptionFoodItemFragment, OrderShefFragment } from 'src/gqlReactTypings.generated.d';
import { FoodItemWithOptionalQuantity } from 'src/pages/consumer/order/components/ConsumerOrderDishSelection';
import { useTracker } from 'src/shared/hooks/useTracker';
import { getSlug } from 'src/shared/utils/RouteUtilities';
import { CartFoodItem } from 'src/store/cart/types';
import { getFoodItemToTrack } from 'src/store/cart/utils/tracking';
import { usePreviousPage } from 'src/util/history';
import { match as tsmatch } from 'ts-pattern';
import { useCachedZipCode } from '../../../../../user-preferences/useCachedZipCode';
import { DishAvailabilityStatus } from '../AddToCartButton';
import { ExploreDishModalWithZip } from '../ExploreDishModalWithZip';
import { ConsumerOrderDishSelectionItemFormat, DishActionsSection } from './DishActionsSection';
import { DishInfoSection } from './DishInfoSection';
import { useGetFoodItemPath } from './hooks';
import { DishCard, DishItemContainer, DishSelectionItem } from './styles';

export type DishSelectionFoodItem = CartFoodItem & FoodItemWithOptionalQuantity & DishDescriptionFoodItemFragment;

interface IProps {
  checkout?: (checkout?: boolean) => void;
  decrement?: (foodItem: DishSelectionFoodItem) => void;
  deliveryDate?: DateTime;
  foodItem: DishSelectionFoodItem;
  format: ConsumerOrderDishSelectionItemFormat;
  increment?: (foodItem: DishSelectionFoodItem) => void;
  isUnavailable?: boolean;
  location: RouteChildrenProps['location'];
  match: RouteChildrenProps['match'];
  quantity: number;
  remainingQuantity: number | null | undefined;
  shef?: OrderShefFragment;
  showDishTags: boolean;
  subtotal?: number;
  showPadding?: boolean;
  isFluid?: boolean;
  isPopular?: boolean;
  shortCard?: boolean;
  showDetails?: boolean;
  isShefAvailable?: boolean;
  nextDeliveryDate?: DateTime | null;
  isShefAvailableInFuture?: boolean;
  openModal?: () => void;
  showModalPrompt?: boolean;
  dishBadge?: React.ReactElement;
  autoWidth?: boolean;
  isEditingOrder: boolean;
}

export const ConsumerOrderDishSelectionItem: React.FC<IProps> = ({
  autoWidth = false,
  deliveryDate,
  dishBadge,
  foodItem,
  format,
  isEditingOrder,
  isFluid = true,
  isShefAvailable,
  isShefAvailableInFuture,
  isUnavailable = false,
  location,
  match,
  nextDeliveryDate = null,
  quantity,
  remainingQuantity,
  shef,
  shortCard = false,
  showDetails = true,
  showDishTags,
  showModalPrompt = false,
  showPadding = true,
  subtotal,
  checkout,
  decrement,
  increment,
  openModal,
}) => {
  const history = useHistory();
  const foodItemSlug = getSlug(match, Slugs.NAME);
  const showFoodItem = Boolean(foodItemSlug && foodItemSlug === foodItem.slug);
  const tracker = useTracker();
  const [, , zipCodeMetaData] = useCachedZipCode();
  const { minimumDiscountedShippingAmount } = zipCodeMetaData;
  const { previousUrl } = usePreviousPage();
  const { shefPath, foodItemPath } = useGetFoodItemPath({
    foodItemSlug: foodItem.slug,
    foodItemShefId: shef?.id,
    location,
    match,
    isEditingOrder,
  });

  // If someone landed on the food item url directly, we want to add the shef's path to the stack to allow back to
  // take them to the shef's page. BTW this may not work in Chrome as Chrome does some shortcutting based on how long
  // someone was on a URL and skips pages when doing back...
  // Note this will become a problem if we start linking directly to food items on our site. The right way to do this
  // is probably to use redux to store history.
  useEffect(() => {
    if ((!location.state as any)?.isShefPageInHistory && location.pathname.includes(foodItemPath)) {
      // Someone has landed directly on the food item path, let's add the shef's page to the history stack
      history.replace(shefPath);
      history.push(location.pathname, { isShefPageInHistory: true, scrollToTop: false });
    }
    // Only want to run this when mounting the component for the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFoodModal = useCallback(() => {
    if (format !== ConsumerOrderDishSelectionItemFormat.ORDER) return;

    history.push(foodItemPath, { isShefPageInHistory: true, scrollToTop: false });
  }, [format, foodItemPath, history]);

  const handleIncrement = useCallback(() => {
    if (!increment) return;
    increment(foodItem);
  }, [foodItem, increment]);

  const handleDecrement = useCallback(() => decrement?.(foodItem), [foodItem, decrement]);

  const handleQuickAddToCart = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e?.preventDefault();
      if (increment) {
        tracker.track(ClientEvents.FREE_DELIVERY_ADD_ITEM, getFoodItemToTrack(foodItem));
        increment(foodItem);
      }
      const hasEnoughSubtotalForMovingOn =
        minimumDiscountedShippingAmount === null || // Null minimum means there is no free shipping
        (subtotal && subtotal + foodItem.price >= minimumDiscountedShippingAmount);
      if (hasEnoughSubtotalForMovingOn && checkout) {
        checkout(true);
      }
    },
    [increment, subtotal, foodItem, minimumDiscountedShippingAmount, checkout, tracker]
  );

  const availabilityStatus = tsmatch({ isUnavailable, remainingQuantity: remainingQuantity ?? 0 })
    .with({ isUnavailable: true }, () => DishAvailabilityStatus.UNAVAILABLE)
    .with({ remainingQuantity: lte(0 as number) }, () => DishAvailabilityStatus.SOLD_OUT)
    .otherwise(() => DishAvailabilityStatus.AVAILABLE);

  const column = format === ConsumerOrderDishSelectionItemFormat.ORDER ? '4' : '6';
  const itemAvailability = foodItem.availability.find(({ numAvailable }) => (numAvailable ?? 0) > 0) ?? null;
  const nextDishDeliveryDate = itemAvailability?.availabilityDate
    ? DateTime.fromISO(itemAvailability.availabilityDate)
    : null;

  const nextDishDeliveryDateString = nextDishDeliveryDate ? formatDateTimeUrlDate(nextDishDeliveryDate) : undefined;
  const dishDeliveryDate =
    isShefAvailable && deliveryDate ? assertDateString(deliveryDate?.toISODate()) : nextDishDeliveryDateString;
  const numItemAvailable = itemAvailability?.numAvailable;

  return (
    <>
      <DishItemContainer
        sm={column}
        data-cy={`foodItem-${foodItem.id}`}
        showPadding={showPadding}
        autoWidth={autoWidth}>
        {shef ? (
          <DishCard
            foodItemPreview={{ ...foodItem, shef }}
            increment={handleIncrement}
            decrement={handleDecrement}
            onClick={openFoodModal}
            minQuantity={0}
            maxQuantity={numItemAvailable ?? remainingQuantity ?? 0}
            quantity={quantity}
            isFluid={isFluid}
            showDescription={false}
            showShefInfo={false}
            shortCard={shortCard}
            showDetails={showDetails}
            isShefAvailable={isShefAvailable}
            nextDeliveryDate={nextDishDeliveryDate ?? nextDeliveryDate}
            isShefAvailableInFuture={isShefAvailableInFuture}
            dishBadge={dishBadge}
          />
        ) : (
          <DishSelectionItem
            showPointer={format === ConsumerOrderDishSelectionItemFormat.ORDER}
            data-cy={`foodItem-desktop-view`}>
            <DishInfoSection
              onClick={openFoodModal}
              showDishTags={showDishTags}
              availabilityStatus={availabilityStatus}
              isUnavailable={isUnavailable}
              foodItem={foodItem}
            />

            <DishActionsSection
              format={format}
              quantity={quantity}
              availability={availabilityStatus}
              onDetailsClick={openFoodModal}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              onAddToCart={handleQuickAddToCart}
            />
          </DishSelectionItem>
        )}
      </DishItemContainer>
      {showFoodItem && (
        <ExploreDishModalWithZip
          deliveryDate={dishDeliveryDate ?? currentUrlDate()}
          cartActionLocation='consumer-order'
          foodItem={foodItem}
          quantity={quantity}
          remainingQuantity={remainingQuantity}
          shefId={shef?.id}
          isShowing={showFoodItem}
          isEditingOrder={isEditingOrder}
          onClose={() => {
            if (previousUrl) {
              history.goBack();
            } else {
              history.replace(shefPath, { scrollToTop: false });
            }
          }}
          openModal={openModal}
          showModalPrompt={showModalPrompt}
        />
      )}
    </>
  );
};
